import { graphql } from 'gatsby';
import { memo } from 'react';
import { type ImageItem } from '@kontent-ai/gatsby-components/dist/image-element';
import { breakpoints } from '@/constants/breakpoints';
import getImage from './utils/getImage';
import getImageAlt from './utils/getImageAlt';
import getSrcSet from './utils/getSrcSet';
import { StyledImage, StyledPicture } from './ResponsiveImage.styles';

interface ResponsiveImageProps extends Queries.RESPONSIVE_IMAGEFragment {
  alt?: string;
  className?: string;
  decoding?: 'async' | 'auto' | 'sync';
  fit?: 'contain' | 'cover';
}

const ResponsiveImage = ({
  alt,
  className,
  decoding = 'async',
  fit = 'cover',
  responsiveImageElements: { mobile, tablet, desktop, loading_strategy },
  system,
}: ResponsiveImageProps) => {
  const sanitizedAlt = getImageAlt({ mobile, tablet, desktop, fallback: alt });

  const mobileImage = getImage({
    image: mobile.value as ImageItem,
    id: 'mobile',
    sourceImageDPR: 2,
  });

  const tabletImage = getImage({
    image: tablet.value as ImageItem,
    id: 'tablet',
    sourceImageDPR: 2,
  });

  const desktopImage = getImage({
    image: desktop.value as ImageItem,
    id: 'desktop',
    sourceImageDPR: 2,
  });

  return (
    <StyledPicture
      style={
        {
          '--mobile-ratio-width': Math.round(mobileImage[0].image.width),
          '--mobile-ratio-height': Math.round(mobileImage[0].image.height),
          '--tablet-ratio-width': Math.round(tabletImage[0].image.width),
          '--tablet-ratio-height': Math.round(tabletImage[0].image.height),
          '--desktop-ratio-width': Math.round(desktopImage[0].image.width),
          '--desktop-ratio-height': Math.round(desktopImage[0].image.height),
        } as React.CSSProperties
      }
      className={className}
      data-codename={system.codename}
    >
      <source
        media={`(min-width: ${breakpoints.DESKTOP}px)`}
        width={desktop.value.width || undefined}
        height={desktop.value.height || undefined}
        srcSet={getSrcSet(desktopImage)}
      />
      <source
        media={`(min-width: ${breakpoints.TABLET}px)`}
        width={tablet.value.width || undefined}
        height={tablet.value.height || undefined}
        srcSet={getSrcSet(tabletImage)}
      />
      <source
        width={mobile.value.width || undefined}
        height={mobile.value.height || undefined}
        srcSet={getSrcSet(mobileImage)}
      />
      <StyledImage
        fit={fit}
        decoding={decoding}
        alt={sanitizedAlt}
        loading={loading_strategy.value}
        srcSet={getSrcSet(mobileImage)}
        width={mobile.value.width || undefined}
        height={mobile.value.height || undefined}
      />
    </StyledPicture>
  );
};

export const fragment = graphql`
  fragment RESPONSIVE_IMAGE on kontent_item_responsive_image {
    responsiveImageElements: elements {
      mobile {
        value {
          description
          height
          name
          type
          url
          width
        }
      }
      tablet {
        value {
          description
          height
          name
          type
          url
          width
        }
      }
      desktop {
        value {
          description
          height
          name
          type
          url
          width
        }
      }
      loading_strategy {
        value
      }
    }
    system {
      codename
    }
  }
`;

export default memo(ResponsiveImage);
